import { FC, useEffect, useState } from "react";

import {
  Box,
  Column,
  DynamicTableIcon,
  IconButton,
  RefreshIcon,
  Row,
  SearchInput,
  Spinner,
  TableIcon,
  Text,
  Tooltip,
} from "@hightouch/htui";

import { IntegrationIcon } from "src/components/integrations/integration-icon";

import { SelectorRow } from "./selector-row";
import {
  getObjectDefinitionKey,
  SourceConfig,
  useObjectDefinitions,
} from "src/utils/sources";
import { QueryType } from "src/types/models";
import { useDebounce } from "use-debounce";
import {
  QueryContainer,
  QueryHeader,
  QueryHeaderTitle,
} from "src/components/query";

type Props = {
  onChange: (table: string) => void;
  table: string | null | undefined;
  source: {
    id?: string | number;
    name: string;
    config: SourceConfig;
    definition: {
      type: string;
      icon: string;
      name: string;
      supportsCrossDatabaseDiscovery: boolean;
      supportedQueries: string[];
    };
  };
};

export const TableSelector: FC<Readonly<Props>> = ({
  table,
  onChange,
  source,
}) => {
  const [search, setSearch] = useState<string>("");
  const [initialReload, setInitialReload] = useState(false);

  const [debouncedSearch, { isPending: isSearchPending }] = useDebounce(
    search,
    1000,
  );

  const {
    loading: objectDefinitionsLoading,
    refetch,
    objectDefinitions,
  } = useObjectDefinitions(
    String(source?.id),
    source?.definition?.supportedQueries.includes(QueryType.Table),
    debouncedSearch,
    { matchDatabase: source?.definition?.supportsCrossDatabaseDiscovery },
  );

  const isLoading = objectDefinitionsLoading || isSearchPending();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!isLoading && !objectDefinitions?.length && !search && !initialReload) {
      refetch();
      setInitialReload(true);
    }
  }, [isLoading, objectDefinitions, initialReload, search]);

  const isPlaceholder = isLoading || !objectDefinitions?.length;

  return (
    <QueryContainer>
      <QueryHeader>
        <QueryHeaderTitle
          icon={
            <IntegrationIcon
              src={source?.definition.icon}
              name={source?.definition.name}
            />
          }
          title={source?.name ?? "Private source"}
        />
        <Row gap={2}>
          <SearchInput
            placeholder="Search tables..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Tooltip
            message="Source schema is refreshing"
            isDisabled={!isLoading}
          >
            <IconButton
              variant="secondary"
              aria-label="Refresh tables"
              icon={RefreshIcon}
              isLoading={isLoading}
              onClick={() => {
                refetch();
              }}
            />
          </Tooltip>
        </Row>
      </QueryHeader>
      <Column
        overflow="hidden"
        flex={1}
        minWidth={0}
        p={isPlaceholder ? 10 : undefined}
        align={isPlaceholder ? "center" : undefined}
        justify={isPlaceholder ? "center" : undefined}
      >
        {isLoading ? (
          <Spinner size="md" />
        ) : !objectDefinitions?.length ? (
          <>
            <Text size="lg" fontWeight="semibold">
              {search ? "No tables match your search" : "No tables found"}
            </Text>
            <Text color="text.secondary">
              Try refreshing to populate the tables
            </Text>
          </>
        ) : (
          <Column
            role="listbox"
            aria-label="Tables"
            overflow="auto"
            flex={1}
            minWidth={0}
          >
            {objectDefinitions?.map(
              ({ name, object_definition_group, meta }) => {
                const key = getObjectDefinitionKey(
                  { name, object_definition_group },
                  source,
                );
                const active = key === table;

                return (
                  <SelectorRow
                    key={key}
                    icon={
                      <Box
                        as={TableIcon}
                        fontSize="16px"
                        color="text.secondary"
                      />
                    }
                    selected={active}
                    end={
                      meta?.isStreamable ? (
                        <Box
                          as={DynamicTableIcon}
                          fontSize="16px"
                          color="text.secondary"
                        />
                      ) : undefined
                    }
                    onClick={() => onChange(key)}
                  >
                    {key}
                  </SelectorRow>
                );
              },
            )}
          </Column>
        )}
      </Column>
    </QueryContainer>
  );
};
